import React, { Fragment, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import Box from '@mui/material/Box';

import {
  Typography,
  colors,
  Input,
  Radio,
  Button,
  Alert,
  Tooltip,
  IconGraphy,
} from '@leapeasy/ui-kit';
import ClaimDatePickerInput from 'components/ClaimForm/ClaimDatePickerInput';

import { setDataAction } from 'store/actions/claimFormActions';
import {
  formatDateForPicker,
  isDateOnOrAfter,
  isDateOnOrBefore,
  isValidPickerDate,
  parsePickerDate,
} from 'components/ClaimForm/helpers';

const MIN_LOSS_DATE = new Date(2018, 0, 1);

const Form3 = (props) => {
  const { actions, formData, handleNext, handleBack } = props;
  const [vacantCheck, setVacantCheck] = useState('No');
  const [evictionCheck, setEvictionCheck] = useState('No');
  const [keyCheck, setKeyCheck] = useState('No');
  const [rentDate, setRentDate] = useState(null);
  const [evictionDate, setEvictionDate] = useState(null);
  const [lateFiling, setLateFiling] = useState(false);
  const [lateFilingExplanation, setLateFilingExplanation] = useState('');
  const [rentDateError, setRentDateError] = useState('');
  const [evictionDateError, setEvictionDateError] = useState('');

  const today = new Date();
  const maxLossDate = formatDateForPicker(today);

  useEffect(() => {
    setVacantCheck(formData.data.vacantCheck || 'No');
    setEvictionCheck(formData.data.evictionCheck || 'No');
    setKeyCheck(formData.data.keyCheck || 'No');
    setRentDate(formData.data.rentDate || null);
    setEvictionDate(formData.data.evictionDate || null);
    setLateFilingExplanation(formData.data.lateFilingExplanation);
    setLateFiling(formData.data.lateFiling);
  }, [formData]);

  const validateRentDate = (value) => {
    if (!value) {
      return 'Please select a date of loss';
    }
    if (!isValidPickerDate(value)) {
      return 'Please enter a valid date (MM/DD/YYYY)';
    }
    if (!isDateOnOrAfter(value, MIN_LOSS_DATE)) {
      return 'Date of loss must be on or after 01/01/2018';
    }
    if (!isDateOnOrBefore(value, today)) {
      return 'Date of loss cannot be in the future';
    }
    return '';
  };

  const validateEvictionDate = (value) => {
    if (!value) {
      return 'Please select the eviction filing date';
    }
    if (!isValidPickerDate(value)) {
      return 'Please enter a valid date (MM/DD/YYYY)';
    }
    if (!isDateOnOrAfter(value, MIN_LOSS_DATE)) {
      return 'Eviction date must be on or after 01/01/2018';
    }
    return '';
  };

  const handleRentDate = (e) => {
    const value = e.target.value;
    const parsed = parsePickerDate(value);

    if (parsed) {
      const date2 = new Date();
      date2.setHours(0, 0, 0, 0);
      const date1 = new Date(parsed);
      date1.setHours(0, 0, 0, 0);
      setLateFiling((date2 - date1) / (1000 * 60 * 60 * 24) >= 120);
    } else {
      setLateFiling(false);
    }

    setRentDate(value);
    setRentDateError(validateRentDate(value));
  };

  const handleKeyCheck = (event) => {
    setKeyCheck(event.target.value);
  };

  const handleVacantCheck = (event) => {
    if (event.target.value === 'No') {
      setKeyCheck('No');
    }
    setVacantCheck(event.target.value);
  };

  const handleEvictionCheck = (event) => {
    if (event.target.value === 'No') {
      setEvictionDate(null);
      setEvictionDateError('');
    }
    setEvictionCheck(event.target.value);
  };

  const handleEvictionDate = (e) => {
    const value = e.target.value;
    setEvictionDate(value);
    setEvictionDateError(validateEvictionDate(value));
  };

  const handleNextButton = () => {
    actions.setDataAction({
      ...formData.data,
      vacantCheck: vacantCheck,
      evictionCheck: evictionCheck,
      keyCheck: keyCheck,
      rentDate: rentDate,
      evictionDate: evictionDate,
      lateFiling: lateFiling,
      lateFilingExplanation: lateFilingExplanation,
    });
    handleNext();
  };

  const rentDateIsValid = !validateRentDate(rentDate);
  const evictionDateIsValid = evictionCheck !== 'Yes' || !validateEvictionDate(evictionDate);
  const lateFilingIsValid = !lateFiling || Boolean(lateFilingExplanation?.trim());

  const disable = !rentDateIsValid || !evictionDateIsValid || !lateFilingIsValid;

  return (
    <Fragment>
      <Box display="flex" flexDirection="column" gap="4px">
        <Box>
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            <Typography variant="small" style={{ paddingLeft: '4px', color: colors.neutral[900] }}>
              Date of Loss
            </Typography>
            <Tooltip
              placement="bottom"
              showCarot
              title={`The first day of the month when the resident failed to pay their full rent balance. For Damage Only claims, the date of loss would be the resident’s move-out date.`}
              variant="contained"
            >
              <IconGraphy
                width="10px"
                height="10px"
                icon={'General.QuestionMark'}
                style={{
                  cursor: 'pointer',
                  marginBottom: '2px',
                  border: '1px solid #2E0F40',
                  borderRadius: '50px',
                }}
              />
            </Tooltip>
          </Box>

          <ClaimDatePickerInput
            value={rentDate || ''}
            minWidth="100%"
            minDate="01/01/2018"
            maxDate={maxLossDate}
            size="medium"
            showActionButtons={false}
            onChange={(e) => handleRentDate(e)}
            placeholder="MM/DD/YYYY"
            fullWidth
          />
          {rentDateError && (
            <Typography
              variant="small"
              style={{ color: colors.error?.[500] || '#d32f2f', paddingLeft: '4px' }}
            >
              {rentDateError}
            </Typography>
          )}
        </Box>

        {lateFiling && (
          <>
            <Typography variant="body1" style={{ color: colors.purple[900] }}>
              The date of loss you have entered is greater than 120 days. Please provide an
              explanation for the reason for late filing
            </Typography>

            <Input
              value={lateFilingExplanation}
              onChange={(e) => setLateFilingExplanation(e.target.value)}
              type="text"
              minWidth="100%"
              fullWidth
              size="medium"
              showCopy={false}
            />

            <Alert
              showIcon
              showClose={false}
              showBorder={false}
              type="info"
              variant="contained"
              description={`Please upload any late notice support documentation under ‘Other Documents’ on page(5)`}
            />
          </>
        )}

        <Box display="flex" flexDirection="column" gap="12px" mb={2}>
          <Typography variant="body1" style={{ color: colors.purple[900] }}>
            Has the resident left the unit?
          </Typography>

          <Box display="flex" sx={{ gap: '24px' }}>
            <Radio
              label="Yes"
              disabled={formData.data.claimType.noShow}
              value={vacantCheck === 'Yes'}
              onChange={(_) => handleVacantCheck({ target: { value: 'Yes' } })}
            />

            <Radio
              label="No"
              disabled={formData.data.claimType.noShow}
              value={vacantCheck === 'No'}
              onChange={(_) => handleVacantCheck({ target: { value: 'No' } })}
            />
          </Box>
        </Box>

        {vacantCheck === 'Yes' && (
          <Box display="flex" flexDirection="column" gap="12px" mb={2}>
            <Typography variant="body1" style={{ color: colors.purple[900] }}>
              Have the keys been returned?
            </Typography>

            <Box display="flex" sx={{ gap: '24px' }}>
              <Radio
                label="Yes"
                value={keyCheck === 'Yes'}
                onChange={(_) => handleKeyCheck({ target: { value: 'Yes' } })}
              />

              <Radio
                label="No"
                value={keyCheck === 'No'}
                onChange={(_) => handleKeyCheck({ target: { value: 'No' } })}
              />
            </Box>
          </Box>
        )}

        <Box display="flex" flexDirection="column" gap="12px" mb={2}>
          <Typography variant="body1" style={{ color: colors.purple[900] }}>
            Have you filed for eviction?
          </Typography>

          <Box display="flex" sx={{ gap: '24px' }}>
            <Radio
              disabled={formData.data.claimType.noShow}
              label="Yes"
              value={evictionCheck === 'Yes'}
              onChange={(_) => handleEvictionCheck({ target: { value: 'Yes' } })}
            />

            <Radio
              disabled={formData.data.claimType.noShow}
              label="No"
              value={evictionCheck === 'No'}
              onChange={(_) => handleEvictionCheck({ target: { value: 'No' } })}
            />
          </Box>
        </Box>

        {evictionCheck === 'Yes' && (
          <Box>
            <ClaimDatePickerInput
              label="Date you filed for eviction"
              value={evictionDate || ''}
              minWidth="100%"
              minDate="01/01/2018"
              maxDate={maxLossDate}
              size="medium"
              showActionButtons={false}
              onChange={(e) => handleEvictionDate(e)}
              placeholder="MM/DD/YYYY"
              fullWidth
            />
            {evictionDateError && (
              <Typography
                variant="small"
                style={{ color: colors.error?.[500] || '#d32f2f', paddingLeft: '4px' }}
              >
                {evictionDateError}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <Box
        sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row', mt: 4, gap: '16px' }}
      >
        <Button size="medium" variant="secondary" onClick={handleBack} style={{ flexShrink: 2 }}>
          Back
        </Button>

        <Button
          size="medium"
          variant="primary"
          onClick={handleNextButton}
          disabled={disable}
          style={{ flexShrink: 1 }}
        >
          Next
        </Button>
      </Box>
    </Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    formData: state.getIn(['formData']),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: bindActionCreators(
      {
        setDataAction,
      },
      dispatch,
    ),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Form3);
